/**
 * Contenido por secciones (shared/section-content.ts): las tarjetas de cada sección y,
 * para quien puede editar, el lápiz por sección, subir/bajar y agregar sección. Lo usan
 * el Inicio de Owned Media y la ficha de cada solución del Org Service (03/11/2026).
 *
 * Guarda SIEMPRE el contenido entero (`onPersist`): el backend lo condiciona a la
 * versión abierta y avisa el conflicto si alguien más guardó en el medio.
 */
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowDown, ArrowUp, ExternalLink, Pencil, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { WinclapLoader } from '@/components/ui/winclap-loader';
import { BrainMarkdown } from '@/components/brain/BrainMarkdown';
import type { Section, SectionItem } from '@/../../shared/section-content';

const k = 'sectionContent';
/**
 * El texto de una sección es markdown liviano (la landing de People lo trae así); los saltos de línea
 * simples se respetan como antes (el Inicio de OML se escribió con enters, no con párrafos).
 */
const keepBreaks = (md: string) => md.replace(/\r/g, '').replace(/([^\n])\n(?!\n)/g, '$1  \n');
const emptyItem = (): SectionItem => ({ title: '', description: '', url: '', tag: '' });

function move<T>(xs: T[], i: number, d: -1 | 1): T[] {
  const j = i + d;
  if (j < 0 || j >= xs.length) return xs;
  const out = [...xs];
  [out[i], out[j]] = [out[j], out[i]];
  return out;
}

/** Edición de UNA sección: título, texto y tarjetas. */
function SectionEditor({ section, onCancel, onSave, onDelete, saving, audienceNote }: {
  section: Section;
  onCancel: () => void;
  onSave: (s: Section) => void;
  onDelete?: () => void;
  saving: boolean;
  audienceNote: string;
}) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<Section>(section);
  const setItem = (i: number, patch: Partial<SectionItem>) =>
    setDraft((d) => ({ ...d, items: d.items.map((it, j) => (j === i ? { ...it, ...patch } : it)) }));

  return (
    <Dialog open onOpenChange={(o) => { if (!o && !saving) onCancel(); }}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
        <DialogHeader><DialogTitle>{t(`${k}.edit_section`, 'Editar sección')}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Input id="section-content-title" value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} placeholder={t(`${k}.title_ph`, 'Título de la sección')} />
          <Textarea id="section-content-intro" value={draft.intro} rows={3} onChange={(e) => setDraft({ ...draft, intro: e.target.value })} placeholder={t(`${k}.intro_ph`, 'Texto de la sección (opcional)')} />
          <div className="space-y-2">
            {draft.items.map((it, i) => (
              <div key={i} className="rounded-lg border p-2 space-y-2">
                <div className="grid gap-2 sm:grid-cols-[1fr_140px]">
                  <Input value={it.title} onChange={(e) => setItem(i, { title: e.target.value })} placeholder={t(`${k}.item_title_ph`, 'Título')} />
                  <Input value={it.tag} onChange={(e) => setItem(i, { tag: e.target.value })} placeholder={t(`${k}.item_tag_ph`, 'Rótulo (Deck, Slack…)')} />
                </div>
                <Textarea value={it.description} rows={2} onChange={(e) => setItem(i, { description: e.target.value })} placeholder={t(`${k}.item_desc_ph`, 'Descripción')} />
                <div className="flex items-center gap-2">
                  <Input value={it.url} onChange={(e) => setItem(i, { url: e.target.value })} placeholder="https://…" className="flex-1" />
                  <Button size="icon" variant="ghost" aria-label={t(`${k}.up`, 'Subir')} disabled={i === 0} onClick={() => setDraft((d) => ({ ...d, items: move(d.items, i, -1) }))}><ArrowUp className="h-4 w-4" /></Button>
                  <Button size="icon" variant="ghost" aria-label={t(`${k}.down`, 'Bajar')} disabled={i === draft.items.length - 1} onClick={() => setDraft((d) => ({ ...d, items: move(d.items, i, 1) }))}><ArrowDown className="h-4 w-4" /></Button>
                  <Button size="icon" variant="ghost" aria-label={t(`${k}.remove_item`, 'Quitar tarjeta')} onClick={() => setDraft((d) => ({ ...d, items: d.items.filter((_, j) => j !== i) }))}><Trash2 className="h-4 w-4" /></Button>
                </div>
              </div>
            ))}
            <Button size="sm" variant="outline" onClick={() => setDraft((d) => ({ ...d, items: [...d.items, emptyItem()] }))}>
              <Plus className="h-4 w-4 mr-1" />{t(`${k}.add_item`, 'Agregar tarjeta')}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">{audienceNote}</p>
        </div>
        <DialogFooter className="gap-2 sm:justify-between">
          {onDelete ? <Button variant="ghost" className="text-destructive" disabled={saving} onClick={() => { if (window.confirm(t(`${k}.delete_confirm`, '¿Borrar la sección "{{title}}"? Se puede volver a armar, pero el contenido se pierde.', { title: section.title }))) onDelete(); }}><Trash2 className="h-4 w-4 mr-1" />{t(`${k}.delete_section`, 'Borrar sección')}</Button> : <span />}
          <div className="flex gap-2">
            <Button variant="outline" disabled={saving} onClick={onCancel}>{t('common.cancel', 'Cancelar')}</Button>
            <Button disabled={saving || !draft.title.trim()} onClick={() => onSave(draft)}>{saving ? <WinclapLoader size={14} /> : t('common.save', 'Guardar')}</Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function SectionContentBoard({ sections, canEdit, saving, onPersist, audienceNote, editing, onEditingChange, emptySection }: {
  sections: Section[];
  canEdit: boolean;
  saving: boolean;
  onPersist: (next: Section[]) => void;
  /** Debajo del editor: quién lo lee (por eso nada de claves). */
  audienceNote: string;
  /** Índice de la sección en edición (`sections.length` = sección nueva); lo controla la página para cerrarlo al guardar. */
  editing: number | null;
  onEditingChange: (i: number | null) => void;
  /** Lo que se muestra en una sección sin texto ni tarjetas (la ficha vacía lo usa para invitar a completarla). */
  emptySection?: ReactNode;
}) {
  const { t } = useTranslation();
  const blank: Section = { id: `section-${Date.now()}`, title: '', intro: '', items: [] };

  return (
    <>
      <div className="space-y-5">
        {sections.length === 0 && emptySection && <Card><CardContent className="pt-6">{emptySection}</CardContent></Card>}
        {sections.map((s, i) => (
          <Card key={s.id}>
            <CardHeader className="pb-2 flex flex-row items-start justify-between gap-3 space-y-0">
              <CardTitle className="text-lg">{s.title}</CardTitle>
              {canEdit && (
                <div className="flex shrink-0 gap-1">
                  <Button size="icon" variant="ghost" className="h-8 w-8" aria-label={t(`${k}.up`, 'Subir')} disabled={i === 0 || saving} onClick={() => onPersist(move(sections, i, -1))}><ArrowUp className="h-4 w-4" /></Button>
                  <Button size="icon" variant="ghost" className="h-8 w-8" aria-label={t(`${k}.down`, 'Bajar')} disabled={i === sections.length - 1 || saving} onClick={() => onPersist(move(sections, i, 1))}><ArrowDown className="h-4 w-4" /></Button>
                  <Button size="icon" variant="ghost" className="h-8 w-8" aria-label={t(`${k}.edit_section`, 'Editar sección')} onClick={() => onEditingChange(i)}><Pencil className="h-4 w-4" /></Button>
                </div>
              )}
            </CardHeader>
            <CardContent className="space-y-3">
              {s.intro && <div className="text-sm text-muted-foreground"><BrainMarkdown content={keepBreaks(s.intro)} /></div>}
              {!s.intro && s.items.length === 0 && emptySection}
              {s.items.length > 0 && (
                <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                  {s.items.map((it, j) => {
                    const body = (
                      <>
                        {it.tag && <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{it.tag}</span>}
                        <div className="flex items-center gap-1.5 font-medium text-sm">
                          {it.title}
                          {it.url && <ExternalLink className="h-3.5 w-3.5 text-brand-green" />}
                        </div>
                        {it.description && <p className="text-xs text-muted-foreground mt-0.5">{it.description}</p>}
                      </>
                    );
                    return it.url
                      ? <a key={j} href={it.url} target="_blank" rel="noopener noreferrer" className="block rounded-lg border p-3 transition-colors hover:bg-muted/60">{body}</a>
                      : <div key={j} className="rounded-lg border p-3">{body}</div>;
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {editing !== null && (
        <SectionEditor
          section={sections[editing] ?? blank}
          saving={saving}
          audienceNote={audienceNote}
          onCancel={() => onEditingChange(null)}
          onSave={(next) => onPersist(editing < sections.length ? sections.map((x, i) => (i === editing ? next : x)) : [...sections, next])}
          onDelete={editing < sections.length ? () => onPersist(sections.filter((_, i) => i !== editing)) : undefined}
        />
      )}
    </>
  );
}
